/**
 * financialFormat — small null-safe display helpers for the Financial Viability
 * report renderers. These format ONLY the deterministic values already on
 * `report.financialAnalysis` (the engine stamp) — they NEVER re-derive any
 * LCOE / NPV / IRR / Monte-Carlo math. A null / non-finite value renders as the
 * em-dash sentinel so a withheld metric never prints "NaN".
 */

const NA = '–';

/** Mono stack used across the financial tables / KPI numerals. */
export const FIN_MONO = 'var(--report-body-font, inherit)';

function isFiniteNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** $/MWh — integer rounded (matches finDecision.formatUsdPerMwh). */
export function fmtUsdPerMwh(v: number | null | undefined): string {
  if (!isFiniteNum(v)) return NA;
  return `$${Math.round(v)}/MWh`;
}

/** Signed compact $M — matches finDecision.formatUsdCompactMillions. */
export function fmtUsdMillions(v: number | null | undefined): string {
  if (!isFiniteNum(v)) return NA;
  const millions = v / 1_000_000;
  const sign = millions >= 0 ? '+' : '−';
  return `${sign}$${Math.abs(millions).toFixed(0)}M`;
}

/** Percentage with fixed digits. */
export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (!isFiniteNum(v)) return NA;
  return `${v.toFixed(digits)}%`;
}

/** Probability fraction [0,1] → percent (P(NPV>0) etc.). */
export function fmtProbabilityPct(v: number | null | undefined, digits = 0): string {
  if (!isFiniteNum(v)) return NA;
  return `${(v * 100).toFixed(digits)}%`;
}

/** Years — one decimal. */
export function fmtYears(v: number | null | undefined): string {
  if (!isFiniteNum(v)) return NA;
  return `${v.toFixed(1)} yr`;
}

/** $/kW — integer rounded. */
export function fmtUsdPerKw(v: number | null | undefined): string {
  if (!isFiniteNum(v)) return NA;
  return `$${Math.round(v).toLocaleString('en-US')}/kW`;
}

/** Annual MWh — locale-grouped integer. */
export function fmtMwh(v: number | null | undefined): string {
  if (!isFiniteNum(v)) return NA;
  return `${Math.round(v).toLocaleString('en-US')} MWh`;
}

/** Band → human verdict label (matches OVERALL_LABELS.financial). */
export function bandVerdictLabel(band: 'green' | 'amber' | 'red' | null): string {
  if (band === 'green') return 'Viable';
  if (band === 'amber') return 'Conditional';
  if (band === 'red') return 'Unfinanceable';
  return 'Verdict withheld';
}

/** Band → editorial tone (positive / warning / negative). */
export type FinTone = 'positive' | 'warning' | 'negative' | 'neutral';
export function bandTone(band: 'green' | 'amber' | 'red' | null): FinTone {
  if (band === 'green') return 'positive';
  if (band === 'amber') return 'warning';
  if (band === 'red') return 'negative';
  return 'neutral';
}

/**
 * A deterministic action, split from its machine tokens.
 *
 * `buildFinancialActionPlan` deliberately writes `… owner: X; deadline: Y.`
 * onto each action so the roadmap parser (`parseOwnerDeadline` in
 * `lib/audit/actionDeduper.ts`) can extract owner and deadline with no LLM.
 * They are MACHINE SYNTAX, not prose, so every renderer must strip them before
 * showing the action to a person.
 */
export interface ParsedFinancialAction {
  /** The action with its tokens removed (falls back to the raw string). */
  text: string;
  owner?: string;
  deadline?: string;
}

// CAPTURE regexes read the token value; STRIP regexes also consume the trailing
// delimiter, so removing `owner: X;` does not leave a stray `;` mid-sentence.
// Kept as two pairs because that is exactly what the three copies did — this is
// a de-duplication, not a re-specification.
const ACTION_OWNER_RE = /owner:\s*([^;.]+)/i;
const ACTION_DEADLINE_RE = /deadline:\s*([^;.]+)/i;
const ACTION_OWNER_STRIP_RE = /owner:\s*[^;.]+[;.]?/i;
const ACTION_DEADLINE_STRIP_RE = /deadline:\s*[^;.]+[;.]?/i;

/**
 * Split the persisted `… owner: X; deadline: Y.` tokens for display.
 *
 * SINGLE HOME, ON PURPOSE. This lived in three places at once — the on-screen
 * section, the DOCX exporter and the PPTX builder — each a byte-for-byte copy
 * whose comment promised the other two matched. Three copies of one parser that
 * MUST agree is how a surface quietly starts splitting actions differently from
 * its siblings; the reader then sees an owner in Word that the screen folded
 * into the sentence. Renderers import this; they do not re-implement it.
 */
export function parseFinancialAction(raw: string): ParsedFinancialAction {
  const ownerMatch = raw.match(ACTION_OWNER_RE);
  const deadlineMatch = raw.match(ACTION_DEADLINE_RE);
  const text = raw
    .replace(ACTION_OWNER_STRIP_RE, '')
    .replace(ACTION_DEADLINE_STRIP_RE, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const owner = ownerMatch?.[1]?.trim();
  const deadline = deadlineMatch?.[1]?.trim();
  return {
    text: text || raw,
    ...(owner ? { owner } : {}),
    ...(deadline ? { deadline } : {}),
  };
}

